// Angular
import { Injectable } from '@angular/core';

// Reactive X
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

// Type fest
import { Get } from 'type-fest';

// Internal dependencies
import { distinctUntilDeepChanged } from '../../../core/utils/operators';

import { Device } from '../../../providers/traccar-client';
import { SubscriptionService } from '../../../providers/subscription.service';
import { Tracker } from '../../../providers/tracker-factory.service';
import { TrackerService } from '../../../providers/tracker.service';

import { SubscriptionPlan } from '../models/plan/subscription-plan';
import { SubscriptionPlanDTO } from '../models/dtos/subscription-option-dto';
import { SubscriptionPlanId } from '../models/plan/subscription-plan-id';
import { SubscriptionPricePoint } from '../models/price-point/subscription-price-point';
import { SubscriptionPricePointId } from '../models/price-point/subscription-price-point-id';


@Injectable({
  providedIn: 'root',
})
export class SubscriptionPlansProvider {

  /* ATTRIBUTES */

  private tracker$: Observable<Tracker> = this.trackerService.getSelected();
  private device$: Observable<Device> =  this.tracker$.pipe(switchMap((tracker: Tracker) => tracker.deviceChanges$));

  private subscription$: Observable<Get<Device, 'attributes.subscription'>> = this.device$.pipe(
    map((device: Device) => device.attributes?.subscription ?? undefined),
    startWith(undefined),
  );

  private subscriptionPlanDTOs$: Observable<SubscriptionPlanDTO[]> = this.device$.pipe(
    distinctUntilChanged((a, b) => a.uniqueId === b.uniqueId),
    switchMap((device: Device) => this.subscriptionService.getPlans(device.uniqueId)),
  );

  public subscriptionAddOns$: Observable<string[]> = this.subscription$.pipe(
    map((subscription) => subscription?.addons?.map((addon) => addon.id) ?? []),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public readonly subscriptionPlans$: Observable<SubscriptionPlan[]> = combineLatest([
    this.subscriptionPlanDTOs$,
    this.subscription$,
  ]).pipe(
    map(([subscriptionPlanDTOs, subscription]) => {
      const biketraxOnlyPlans = this.parseBiketraxOnlyPlans(subscriptionPlanDTOs);
      const biketraxOnlyPlansWithActivationFee = this.parseBiketraxOnlyPlansWithActivationFee(subscriptionPlanDTOs);
      const biketraxRecoveryPlans = this.parseBiketraxRecoveryPlans(subscriptionPlanDTOs);

      // if there is already a subscription (regardless if it is cancled or not) we should not checkout a new bike trax only one
      if(subscription && subscription.status !== 'cancelled') return biketraxRecoveryPlans;

      return [...biketraxOnlyPlans, ...biketraxOnlyPlansWithActivationFee, ...biketraxRecoveryPlans];
    }),
    distinctUntilDeepChanged(),
    shareReplay(1),
  );

  public readonly isBiketraxRecoveryPlanAvailable$: Observable<boolean> = this.subscriptionPlans$.pipe(
    map((subscriptionPlans: SubscriptionPlan[]) => {
      return subscriptionPlans.find((subscriptionPlan) =>
        subscriptionPlan.id === SubscriptionPlanId.BIKETRAX_WITH_RECOVERY) !== undefined;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );


  /* LIFECYCLE */

  public constructor(
    private subscriptionService: SubscriptionService,
    private trackerService: TrackerService,
  ) {}


  /* UTLITY */

  private parseBiketraxOnlyPlans(planDTOs: SubscriptionPlanDTO[]): SubscriptionPlan[] {
    let pricePoints: SubscriptionPricePoint[] = [];

    // BikeTrax only subscription plans
    for (const planDTO of planDTOs) {
      // Validation
      if (!planDTO.id.startsWith('biketrax-service-plan')) continue;

      // Price points
      const pricePointId: SubscriptionPricePointId = planDTO.id.replace('biketrax-service-plan-', '');

      pricePoints = [...pricePoints, {
        id: pricePointId,
        currency: planDTO.currency_code,
        frequency: planDTO.period_unit.toUpperCase(),
        price: planDTO.price / 100
        }];
    }

    if (pricePoints.length < 1) return [];

    return [{
      id: SubscriptionPlanId.BIKETRAX_ONLY,
      pricePoints: pricePoints,
      isPricePointOptional: true,
      isCertified: false,
      isNew: false,
    }];
  }

  private parseBiketraxOnlyPlansWithActivationFee(planDTOs: SubscriptionPlanDTO[]): SubscriptionPlan[] {
    let activationFee: number | undefined;
    let activationFeeCurrency: string | undefined;
    let pricePoints: SubscriptionPricePoint[] = [];

    // BikeTrax only subscription plans
    for (const planDTO of planDTOs) {
      // Validation
      if (!planDTO.id.startsWith('setup-biketrax-service-plan')) continue;

      // Activation fee
      activationFee = planDTO.setup_cost / 100;

      // Activation fee currency
      activationFeeCurrency = planDTO.currency_code;

      // Price points
      const pricePointId: SubscriptionPricePointId = planDTO.id.replace('setup-biketrax-service-plan-', '');

      pricePoints = [...pricePoints, {
        id: pricePointId,
        currency: planDTO.currency_code,
        frequency: planDTO.period_unit.toUpperCase(),
        price: planDTO.price / 100,
      }];
    }

    if (pricePoints.length < 1) return [];

    return [{
      id: SubscriptionPlanId.BIKTERAX_ONLY_WITH_ACTIVATION_FEE,
      pricePoints: pricePoints,
      isPricePointOptional: true,
      activationFee: activationFee,
      activationFeeCurrency: activationFeeCurrency,
      isCertified: false,
      isNew: false,
    }];
  }

  private parseBiketraxRecoveryPlans(planDTOs: SubscriptionPlanDTO[]): SubscriptionPlan[] {

    let pricePoints: SubscriptionPricePoint[] = [];

    const plansWithRecovery = planDTOs
    .filter(plan =>plan.id.startsWith('biketrax-service-plan'))
    .filter(plan => plan.applicable_addons?.filter(addon => addon.id.startsWith('AB0-R-'))?.length  > 0);


    // BikeTrax only subscription plans
    for (const planDTO of plansWithRecovery) {
      const addons = planDTO.applicable_addons.filter(addon => addon.id.startsWith('AB0-R-'));
      // Price points
      const pricePointId: SubscriptionPricePointId = planDTO.id.replace('biketrax-service-plan-', '');

      const priceAddons = addons.reduce((acc, cur) => acc + cur.price, 0);

      pricePoints = [...pricePoints, {
        id: pricePointId,
        currency: planDTO.currency_code,
        frequency: planDTO.period_unit.toUpperCase(),
        price: (planDTO.price + priceAddons) / 100
        }];
    }

    if (pricePoints.length < 1) return [];

    return [{
      id: SubscriptionPlanId.BIKETRAX_WITH_RECOVERY,
      pricePoints: pricePoints,
      isPricePointOptional: false,
      isCertified: false,
      isNew: false,
      proratedPriceUntilCurrentTermEnd: plansWithRecovery[0].applicable_addons.filter(addon =>addon.id.startsWith('AB0-R-')).reduce((acc, cur) => acc + cur.proratedPriceUntilCurrentTermEnd, 0) / 100
    }];
  }

}

